import React, { useState, useEffect, Fragment } from "react"
import "./OutcomeMeasure.scss"
import { v4 as uuidv4 } from "uuid"

// components
import Header from "../components/Header/Header"
import StatisticalAnalysisItem from "./StatisticalAnalysisItem/StatisticalAnalysisItem"

const OutcomeMeasure = (props) => {
  const { index, data, bgColor, schema, view, formData } = props
  const [tables, setTables] = useState([])

  useEffect(() => {
    if (data) {
      let startIndex = 0
      let endIndex = 5
      let tables = []

      let allGroups = []
      if (data?.arms) {
        allGroups = [...data.arms]
      }

      if (data?.subject_analysis_sets) {
        allGroups = [...allGroups, ...data.subject_analysis_sets]
      }

      if (allGroups.length > endIndex) {
        while (allGroups.length > startIndex) {
          if (endIndex >= allGroups.length) endIndex = allGroups.length
          let table = {
            ...data,
            arms: allGroups.slice(startIndex, endIndex),
            measure_rows: data.measure_rows.map((row) => {
              return {
                ...row,
                categories: row.categories.map((category) => {
                  return {
                    ...category,
                    values: category.values.slice(startIndex, endIndex),
                  }
                }),
              }
            }),
          }

          tables.push(table)
          startIndex = endIndex
          endIndex += 5
        }
      } else {
        tables = [
          {
            ...data,
            arms: allGroups,
          },
        ]
      }
      setTables(tables)
    }
  }, [data])

  const displayCTGProps = () => {
    return schema.properties[view].properties.outcome_measure.prop_keys
      .filter((key) => key !== "type")
      .map((key) => {
        let obj = schema.properties[view].properties.outcome_measure.properties
        let value = data?.[key]

        if (data?.[key] && typeof data[key] === "object") {
          value = []
          Object.keys(data[key]).forEach((nestedKey) => {
            value.push(data[key][nestedKey])
          })
          value.join(" ")
        }

        return (
          <tr key={`om-${index}-${key}`}>
            <td style={{ width: 250, fontWeight: "bold", backgroundColor: bgColor }}>{obj[key]?.title}</td>
            <td>{value}</td>
          </tr>
        )
      })
  }

  const getDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") return "-"
    return value
  }

  const getCellValue = (category, cell, row, colIndex) => {
    let value = getDisplayValue(cell.value)

    if (category?.title === "Number Analyzed") {
      value += " participants"
      return value
    }
    if (data?.parameter_type?.toLowerCase() === "number" || data?.countable === "Countable") {
      if (data?.dispersion_type?.toLowerCase() === "not applicable" || data?.countable === "Countable") {
        return value
      } else if (data?.dispersion_type?.toLowerCase() === "confidence interval") {
        value += ` (${getDisplayValue(cell.dispersion_value)} to ${getDisplayValue(cell.dispersion_value_upper_bound)})`
        return value
      }
    }

    if (data.parameter_type !== "Count of Participants" && data.parameter_type !== "Number") {
      if (
        data?.dispersion_type?.toLowerCase() === "standard deviation" ||
        data?.dispersion_type?.toLowerCase() === "standard error" ||
        data?.dispersion_type?.toLowerCase() === "geometric coefficient of variation" ||
        data?.countable === "Countable"
      ) {
        value += ` (${getDisplayValue(cell.dispersion_value)})`
      } else {
        value += ` (${getDisplayValue(cell.dispersion_value)} to ${getDisplayValue(cell.dispersion_value_upper_bound)})`
      }
    } else if (
      row.categories?.[0]?.title === "Number Analyzed" &&
      row.categories?.[0]?.values[colIndex].value &&
      cell.value &&
      typeof Number(cell.value) === "number" &&
      Number(cell.value) !== NaN
    ) {
      let percent = 0
      percent = (Number(cell.value) / Number(row.categories[0].values[colIndex].value)) * 100
      if (percent === NaN) percent = 0
      value += ` (${percent?.toFixed(2)}%)`
    }

    return value
  }

  const getMeasureType = () => {
    let value = `${data.parameter_type}`
    if (data.countable === "Countable" && view === "eudract") {
      return "Countable"
    }
    if (data.parameter_type !== "Count of Participants" && data.parameter_type !== "Number") {
      value += ` (${data.dispersion_type || "NA"})`
    }

    if (data?.dispersion_type?.toLowerCase() === "confidence interval") {
      value = `${data.parameter_type} (${data?.ci_pct_pevel}% ${data.dispersion_type})`
    }

    return value
  }

  let firstColumnWidth = 250

  const displayMeasureProps = (table) => {
    let colSpan = 0

    if (table?.arms) {
      colSpan += table.arms.length
    }
    if (table?.subject_analysis_sets) {
      colSpan += table.subject_analysis_sets.length
    }

    return (
      <>
        {view === "ctg" && (
          <tr style={{ backgroundColor: bgColor }}>
            <td
              colSpan={2}
              style={{ fontWeight: "bold", width: firstColumnWidth }}
            >
              Analysis Population Description
            </td>
            <td colSpan={colSpan}>{data?.population_analysis_description}</td>
          </tr>
        )}
        {data?.type_units_analyzed && (
          <tr style={{ backgroundColor: bgColor }}>
            <td
              colSpan={2}
              style={{ fontWeight: "bold", width: firstColumnWidth }}
            >
              Type of Units Analyzed
            </td>
            <td colSpan={colSpan}>{data?.type_units_analyzed}</td>
          </tr>
        )}
      </>
    )
  }

  const getColSpanForHeaderCatColumn = ({ row }) => {
    if (view === "eudract" && row?.categories?.length === 1) {
      return 2
    }
    return 1
  }

  const renderEudractCatCol = ({ row }) => {
    if (view === "eudract" && row?.categories?.length === 1) {
      return false
    }
    return true
  }

  // if units analyzed exists
  let showNumUnitsAnalyzed = false
  if (data.arms) {
    showNumUnitsAnalyzed = !data?.arms?.some((arm) => !arm?.num_units_analyzed)
  }

  return (
    <div className="outcome-measure-outer-container ">
      <h6 className="outcome-measure-title">{`${index}. ${data.measure_type} Outcome Measure`}</h6>
      <div className="outcome-measure-inner-container">
        <table className="outcome-measure-table">
          <tbody>{displayCTGProps()}</tbody>
        </table>
        <div className="outcome-header-container">
          <Header
            title={"Reporting Groups"}
            bgColor={bgColor}
            arms={data?.arms || []}
            subjectAnalysisSets={data?.subject_analysis_sets || null}
            source="outcome"
            data={data}
            view={view}
            formData={formData}
          />
        </div>
        {tables?.map((table, i) => {
          return (
            <table
              key={`om-preview-${index}-${i}`}
              className="outcome-measure-table"
            >
              <tbody>
                {displayMeasureProps(table)}
                <tr style={{ backgroundColor: bgColor, fontWeight: "bold" }}>
                  <td colSpan={2} />
                  {table?.arms && table.arms.map((arm) => <td key={arm.id}>{arm.title}</td>)}
                </tr>
                {view === "eudract" && (
                  <>
                    <tr style={{ backgroundColor: bgColor }}>
                      <td
                        colSpan={2}
                        style={{ fontWeight: "bold", width: firstColumnWidth }}
                      >
                        Subjects Analyzed
                      </td>
                      {table?.arms &&
                        table.arms.map((arm) => (
                          <td key={`${arm.id}-subjects-analyzed`}>
                            {arm.subjects_analyzed}
                            {arm.subjects_analyzed == 0 && (
                              <>
                                <br />
                                {arm.comment}
                              </>
                            )}
                          </td>
                        ))}
                    </tr>
                    {table?.measure_rows?.length === 0 && (
                      <tr style={{ backgroundColor: bgColor }}>
                        <td
                          colSpan={2}
                          className="om-property-cell"
                          style={{ fontWeight: "bold", width: firstColumnWidth }}
                        >
                          <div className="outcome-measure-properties">
                            <p style={{ fontWeight: "bold", fontSize: ".7rem", marginBottom: 0 }}>Measure Type:</p>{" "}
                            {getMeasureType()}
                          </div>
                          <div className="outcome-measure-properties">
                            <p style={{ fontWeight: "bold", fontSize: ".7rem", marginBottom: 0 }}>Unit of Measure:</p>{" "}
                            {table.unit_of_measure}
                          </div>
                        </td>
                        {table?.arms &&
                          table.arms.map((arm, i) => (
                            <td
                              className="om-arm-cell"
                              key={`${arm.id}-subjects-analyzed`}
                            >
                              {getCellValue({}, arm, 0, i)}
                            </td>
                          ))}
                      </tr>
                    )}
                  </>
                )}
                {view === "ctg" && (
                  <tr style={{ backgroundColor: bgColor }}>
                    <td
                      colSpan={2}
                      style={{ fontWeight: "bold", width: firstColumnWidth }}
                    >
                      Overall Number of Participants Analyzed
                    </td>
                    {table?.arms &&
                      table.arms.map((arm) => <td key={`${arm.id}-subjects-analyzed`}>{arm?.subjects_analyzed}</td>)}
                  </tr>
                )}
                {view === "ctg" && showNumUnitsAnalyzed && (
                  <tr style={{ backgroundColor: bgColor }}>
                    <td
                      colSpan={2}
                      style={{ fontWeight: "bold", width: firstColumnWidth }}
                    >
                      Overall Number of Units Analyzed
                    </td>
                    {table?.arms &&
                      table.arms.map((arm) => <td key={`${arm.id}-subjects-analyzed`}>{arm.num_units_analyzed}</td>)}
                  </tr>
                )}
                {table?.measure_rows?.map((row) => {
                  return (
                    <Fragment key={uuidv4()}>
                      {row?.categories?.map((category, i) => {
                        return (
                          <tr key={uuidv4()}>
                            {i === 0 && (
                              <th
                                style={{ backgroundColor: bgColor, width: firstColumnWidth }}
                                colSpan={getColSpanForHeaderCatColumn({ row })}
                                rowSpan={row.categories.length}
                                className="row-header"
                              >
                                <div className="outcome-measure-title-2">{row.title}</div>
                                <div className="outcome-measure-properties">
                                  <p style={{ fontWeight: "bold", fontSize: ".7rem", marginBottom: 0 }}>
                                    Measure Type:
                                  </p>{" "}
                                  {getMeasureType()}
                                </div>
                                <div className="outcome-measure-properties">
                                  <p style={{ fontWeight: "bold", fontSize: ".7rem", marginBottom: 0 }}>
                                    Unit of Measure:
                                  </p>{" "}
                                  {table.unit_of_measure}
                                </div>
                              </th>
                            )}
                            {(table?.arms?.length > 0 || table?.subject_analysis_sets?.length > 0) && (
                              <>
                                {renderEudractCatCol({ row }) && (
                                  <td
                                    colSpan={1}
                                    style={{ backgroundColor: bgColor, width: firstColumnWidth }}
                                    className="cat-title"
                                  >
                                    {category.title}
                                  </td>
                                )}
                                {category?.values?.map((cell, j) => {
                                  let value = getCellValue(category, cell, row, j)
                                  return (
                                    <td key={uuidv4()}>
                                      <div>{value}</div>
                                      {cell.na_comment &&
                                        (value === "NA" ||
                                          cell.dispersion_value === "NA" ||
                                          cell.dispersion_value_upper_bound === "NA") && <div>{cell.na_comment}</div>}
                                    </td>
                                  )
                                })}
                              </>
                            )}
                          </tr>
                        )
                      })}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          )
        })}

        {/* Statistical Analysis Sets */}
        <div className="statistical-analysis-container">
          {data?.outcome_measure_analysis?.map((item, index) => (
            <StatisticalAnalysisItem
              key={`${data.id}-statistical-analysis-item-${index}`}
              index={index}
              data={item}
              schema={schema}
              view={view}
              bgColor={bgColor}
              outcomeMeasure={data}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default OutcomeMeasure
